import React, { useState, useCallback } from "react";
import {
  Box, Paper, Typography, TextField, Button, Stack, Table, TableBody, TableHead,
  TableRow, TableCell, CircularProgress, Divider, Alert, Collapse,
} from "@mui/material";
import api from "../services/api";
import { todayISO } from "../utils/date";

const today = () => todayISO();
const money = (n) =>
  (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const qtyFmt = (n) =>
  (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const rate = (n) =>
  (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 4, maximumFractionDigits: 4 });

export default function InventoryValuation() {
  const [asOf, setAsOf] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showUnvalued, setShowUnvalued] = useState(false);

  const run = useCallback(() => {
    if (!asOf) { setError("Pick a date"); return; }
    setLoading(true); setError(null);
    api.get("/accounting/inventory-valuation", { params: { asOf } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.error || "Failed to build Inventory Valuation"))
      .finally(() => setLoading(false));
  }, [asOf]);

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>Inventory Valuation</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Dollar value of stock on hand = quantity on hand &times; price, per item. Shown at
        <strong> weighted-average cost</strong> and <strong>last purchase price</strong> (these are your
        accounting inventory value), plus an <strong>estimated market value</strong> at your last sale price.
        The cost figures are your Ending Inventory for periodic Cost of Goods Sold.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
          <TextField type="date" size="small" label="As of" InputLabelProps={{ shrink: true }}
            value={asOf} onChange={(e) => setAsOf(e.target.value)} />
          <Button variant="contained" onClick={run} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : "Run report"}
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && !loading && (
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ textAlign: "center", mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Inventory Valuation</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>As of {data.asOf}</Typography>
          </Box>
          <Divider sx={{ mb: 1 }} />

          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ maxWidth: 1180, mx: "auto" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Item</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Qty on hand</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Avg cost</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Value (avg cost)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Last cost</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Value (last cost)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: "success.main" }}>Last sale price</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: "success.main" }}>Market value (est.)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.rows.length === 0 && (
                  <TableRow><TableCell colSpan={8} sx={{ color: "text.secondary" }}>No valued stock on hand.</TableCell></TableRow>
                )}
                {data.rows.map((r, i) => (
                  <TableRow key={`${r.item}-${i}`}>
                    <TableCell sx={{ py: 0.4 }}>{r.item}</TableCell>
                    <TableCell align="right" sx={{ py: 0.4, fontVariantNumeric: "tabular-nums" }}>{qtyFmt(r.qty)}</TableCell>
                    <TableCell align="right" sx={{ py: 0.4, fontVariantNumeric: "tabular-nums" }}>{rate(r.avgCost)}</TableCell>
                    <TableCell align="right" sx={{ py: 0.4, fontVariantNumeric: "tabular-nums" }}>{money(r.value)}</TableCell>
                    <TableCell align="right" sx={{ py: 0.4, fontVariantNumeric: "tabular-nums" }}>{r.lastCost == null ? "—" : rate(r.lastCost)}</TableCell>
                    <TableCell align="right" sx={{ py: 0.4, fontVariantNumeric: "tabular-nums" }}>{money(r.lastValue)}</TableCell>
                    <TableCell align="right" sx={{ py: 0.4, fontVariantNumeric: "tabular-nums", color: r.salePrice == null ? "text.disabled" : "success.main" }}>{r.salePrice == null ? "—" : rate(r.salePrice)}</TableCell>
                    <TableCell align="right" sx={{ py: 0.4, fontVariantNumeric: "tabular-nums", color: r.salePrice == null ? "text.disabled" : "success.main" }}>{r.salePrice == null ? "—" : money(r.saleValue)}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell sx={{ borderTop: "2px solid", borderColor: "text.primary", py: 1, fontWeight: 800 }}>
                    Ending Inventory Value
                  </TableCell>
                  <TableCell colSpan={2} sx={{ borderTop: "2px solid", borderColor: "text.primary" }} />
                  <TableCell align="right" sx={{ borderTop: "2px solid", borderColor: "text.primary", py: 1, fontWeight: 800, fontSize: 16, fontVariantNumeric: "tabular-nums" }}>
                    {money(data.totalValue)}
                  </TableCell>
                  <TableCell sx={{ borderTop: "2px solid", borderColor: "text.primary" }} />
                  <TableCell align="right" sx={{ borderTop: "2px solid", borderColor: "text.primary", py: 1, fontWeight: 800, fontSize: 16, fontVariantNumeric: "tabular-nums" }}>
                    {money(data.totalValueLast)}
                  </TableCell>
                  <TableCell sx={{ borderTop: "2px solid", borderColor: "text.primary" }} />
                  <TableCell align="right" sx={{ borderTop: "2px solid", borderColor: "text.primary", py: 1, fontWeight: 800, fontSize: 16, fontVariantNumeric: "tabular-nums", color: "success.main" }}>
                    {money(data.totalSaleValue)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>

          {data.saleableCount > 0 && (
            <Box sx={{ maxWidth: 1180, mx: "auto", mt: 2, p: 1.5, borderRadius: 1, bgcolor: "action.hover", display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Estimated unrealized margin <em>(market value &minus; avg cost, over the {data.saleableCount} item(s)
                that have a recent sale price)</em>
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, fontVariantNumeric: "tabular-nums", color: data.marginVsCost >= 0 ? "success.main" : "error.main" }}>
                {data.marginVsCost >= 0 ? "" : "−"}{money(Math.abs(data.marginVsCost))}
              </Typography>
            </Box>
          )}

          {data.unvaluedCount > 0 && (
            <Alert severity="info" sx={{ mt: 2, maxWidth: 820, mx: "auto" }}
              action={<Button color="inherit" size="small" onClick={() => setShowUnvalued((s) => !s)}>{showUnvalued ? "Hide" : "Show"}</Button>}>
              <strong>{data.unvaluedCount} item(s)</strong> have stock on hand but were never purchased,
              so there's no cost to value them with (usually processing outputs). They're excluded from
              the total above — we'll decide how to cost these together.
            </Alert>
          )}
          <Collapse in={showUnvalued}>
            <Box sx={{ overflowX: "auto", mt: 1 }}>
              <Table size="small" sx={{ maxWidth: 820, mx: "auto" }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Unvalued item</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Qty on hand</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.unvalued.map((r, i) => (
                    <TableRow key={`u-${r.item}-${i}`}>
                      <TableCell sx={{ py: 0.4 }}>{r.item}</TableCell>
                      <TableCell align="right" sx={{ py: 0.4, fontVariantNumeric: "tabular-nums" }}>{qtyFmt(r.qty)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>

          <Typography variant="caption" sx={{ display: "block", mt: 2, color: "text.secondary" }}>
            Quantity on hand uses the same formula as your Stock Inventory report
            (Purchased &minus; Sold + Regrade In &minus; Regrade Out + Opening). <strong>Avg cost</strong> = total
            purchase dollars &divide; total weight purchased, per item, through the date shown.
            <strong> Last cost</strong> = the price on that item's most recent purchase through the date shown.
            <strong> Market value (est.)</strong> = quantity &times; the price on that item's most recent
            <em> sale</em> through the date shown — an estimate of what today's stock could fetch, <u>not</u> your
            accounting inventory value. Items you've never sold show &ldquo;&mdash;&rdquo; and are left out of the
            market-value total and the margin.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
